import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Tab,
  Tabs,
  Card,
  CardContent,
  Tooltip,
  Autocomplete,
  Grid,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import SyncIcon from '@mui/icons-material/Sync';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolIcon from '@mui/icons-material/School';
import SendIcon from '@mui/icons-material/Send';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useMemberspotTraining from '../../hooks/useMemberspotTraining';
import useRole from '../../hooks/useRole';
import useBranch from '../../hooks/useBranch';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

const DEFAULT_RULE = {
  name: '',
  description: '',
  isEnabled: true,
  targetRoleIds: [],
  targetBranchIds: [],
  memberspotOfferId: '',
  memberspotCourseIds: [],
  requiredExamIds: [],
  triggers: [{ type: 'afterUserCreation', delayDaysAfterUserCreation: 0 }],
  deadlineDaysAfterInvitation: 30,
  setMemberspotExpiryFromDeadline: false,
  applyToExistingUsers: false,
  reminders: [
    { kind: 'employee', daysBeforeDue: 14 },
    { kind: 'employee', daysBeforeDue: 7 },
    { kind: 'employee', daysBeforeDue: 1 },
    { kind: 'manager', daysBeforeDue: 3 },
  ],
  consequence: {
    deactivateMemberspotAccess: false,
    acknowledgementRoleIds: [],
    notifyRoleIds: [],
    internalRestrictionNote: '',
  },
};

function tokensFromCsv(value) {
  return String(value || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function csvFromTokens(tokens) {
  return Array.isArray(tokens) ? tokens.join(', ') : '';
}

export default function MemberspotTrainingManagement() {
  const authCtx = useContext(AuthContext);
  const canManage = authCtx.checkRight('memberspotTraining:update');
  const canSync = authCtx.checkRight('memberspotTraining:special:sync');

  const ms = useMemberspotTraining();
  const roleHook = useRole();
  const branchHook = useBranch();

  const [tab, setTab] = useState(0);
  const [rules, setRules] = useState([]);
  const [offers, setOffers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);

  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [draftRule, setDraftRule] = useState(DEFAULT_RULE);
  const [draftRuleId, setDraftRuleId] = useState(null);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillData, setBackfillData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [offerEditOpen, setOfferEditOpen] = useState(false);
  const [draftOffer, setDraftOffer] = useState(null);
  const [inlineNameOfferId, setInlineNameOfferId] = useState(null);
  const [inlineNameDraft, setInlineNameDraft] = useState({});
  const [mcpStatus, setMcpStatus] = useState(null);
  const [assignmentsDialogRule, setAssignmentsDialogRule] = useState(null);
  const [assignmentsDialogData, setAssignmentsDialogData] = useState(null);
  const [assignmentsDialogLoading, setAssignmentsDialogLoading] = useState(false);
  const [resendingAssignmentId, setResendingAssignmentId] = useState(null);
  const [resendConfirm, setResendConfirm] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [rulesRes, offersRes, rolesRes, branchesRes, mcpRes] = await Promise.all([
        ms.listRules(),
        ms.listOffers(),
        roleHook.getRoles(),
        branchHook.getBranches(),
        ms.getMcpStatus().catch(() => null),
      ]);
      setRules(rulesRes?.rules || []);
      setOffers(offersRes?.offers || []);
      setRoles(Array.isArray(rolesRes) ? rolesRes : rolesRes?.roles || []);
      setBranches(Array.isArray(branchesRes) ? branchesRes : branchesRes?.branches || []);
      setMcpStatus(mcpRes || null);
    } catch {
      // toasts handled inside hooks
    }
  }, [ms, roleHook, branchHook]);

  useEffect(() => {
    if (!canManage) return;
    loadAll();
  }, [canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canManage) return;
    const url = new URL(window.location.href);
    const mcpParam = url.searchParams.get('mcp');
    if (mcpParam === 'connected') {
      toast.success('Memberspot MCP verbunden \u2014 Kursnamen werden jetzt automatisch geladen.');
      url.searchParams.delete('mcp');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
      setTab(1);
      loadAll();
    } else if (mcpParam === 'error') {
      const reason = url.searchParams.get('reason') || 'Unbekannt';
      toast.error(`Memberspot MCP Verbindung fehlgeschlagen: ${reason}`);
      url.searchParams.delete('mcp');
      url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
      setTab(1);
    }
  }, [canManage]); // eslint-disable-line react-hooks/exhaustive-deps

  const offersById = useMemo(() => {
    const map = new Map();
    for (const o of offers) map.set(o.offerId, o);
    return map;
  }, [offers]);

  if (!canManage) return <Navigate to='/welcome' replace />;

  const handleSync = async () => {
    setBusy(true);
    try {
      await ms.syncOffers();
      toast.success('Memberspot Angebote synchronisiert');
      await loadAll();
    } finally {
      setBusy(false);
    }
  };

  const openRuleDialog = rule => {
    if (rule) {
      const incomingTriggers =
        Array.isArray(rule.triggers) && rule.triggers.length > 0
          ? rule.triggers
          : rule.trigger
          ? [rule.trigger]
          : DEFAULT_RULE.triggers;
      setDraftRule({
        ...DEFAULT_RULE,
        ...rule,
        triggers: incomingTriggers.map(t => ({
          type: t.type || 'afterUserCreation',
          delayDaysAfterUserCreation: t.delayDaysAfterUserCreation || 0,
          prerequisiteRuleId: t.prerequisiteRuleId || '',
          delayDaysAfterPrerequisite: t.delayDaysAfterPrerequisite || 0,
        })),
        consequence: { ...DEFAULT_RULE.consequence, ...(rule.consequence || {}) },
        reminders:
          Array.isArray(rule.reminders) && rule.reminders.length > 0
            ? rule.reminders
            : DEFAULT_RULE.reminders,
      });
      setDraftRuleId(rule._id);
    } else {
      setDraftRule(DEFAULT_RULE);
      setDraftRuleId(null);
    }
    setRuleDialogOpen(true);
  };

  const saveRule = async () => {
    if (!draftRule.name?.trim() || !draftRule.memberspotOfferId?.trim()) {
      toast.error('Name und Memberspot Angebot sind erforderlich');
      return;
    }
    setBusy(true);
    try {
      const saved = draftRuleId
        ? await ms.updateRule(draftRuleId, draftRule)
        : await ms.createRule(draftRule);
      const created = saved?.backfillCreated || 0;
      const invited = saved?.backfillInvited || 0;
      const failed = saved?.backfillFailed || 0;
      if (created > 0 || invited > 0) {
        const parts = [];
        if (created > 0) parts.push(`${created} Zuweisungen angelegt`);
        if (invited > 0) parts.push(`${invited} per Memberspot eingeladen`);
        if (failed > 0) parts.push(`${failed} Einladungen fehlgeschlagen`);
        const summary = parts.join(', ');
        if (failed > 0) toast.warning(`Schulungsregel gespeichert \u2014 ${summary}`);
        else toast.success(`Schulungsregel gespeichert \u2014 ${summary}`);
      } else {
        toast.success('Schulungsregel gespeichert');
      }
      setRuleDialogOpen(false);
      await loadAll();
    } finally {
      setBusy(false);
    }
  };

  const openAssignmentsDialog = async rule => {
    setAssignmentsDialogRule(rule);
    setAssignmentsDialogData(null);
    setAssignmentsDialogLoading(true);
    try {
      const data = await ms.listRuleAssignments(rule._id);
      setAssignmentsDialogData(data);
    } finally {
      setAssignmentsDialogLoading(false);
    }
  };

  const refreshAssignmentsDialog = async () => {
    if (!assignmentsDialogRule) return;
    setAssignmentsDialogLoading(true);
    try {
      const data = await ms.listRuleAssignments(assignmentsDialogRule._id);
      setAssignmentsDialogData(data);
    } finally {
      setAssignmentsDialogLoading(false);
    }
  };

  const askResendAssignmentInvitation = assignment => {
    if (!assignment?._id) return;
    setResendConfirm(assignment);
  };

  const confirmResendAssignmentInvitation = async () => {
    const assignment = resendConfirm;
    if (!assignment?._id) return;
    setResendingAssignmentId(assignment._id);
    try {
      const updated = await ms.resendAssignmentInvitation(assignment._id);
      if (!updated) return;
      toast.success('Einladung wurde erneut gesendet');
      setAssignmentsDialogData(prev =>
        prev
          ? {
              ...prev,
              assignments: prev.assignments.map(a => (a._id === updated._id ? updated : a)),
            }
          : prev
      );
      setResendConfirm(null);
    } finally {
      setResendingAssignmentId(null);
    }
  };

  const archiveRule = async ruleId => {
    if (!window.confirm('Schulungsregel deaktivieren? Bestehende Zuweisungen werden archiviert.')) {
      return;
    }
    setBusy(true);
    try {
      await ms.deleteRule(ruleId);
      toast.success('Schulungsregel deaktiviert');
      await loadAll();
    } finally {
      setBusy(false);
    }
  };

  const openBackfill = async ruleId => {
    setBusy(true);
    try {
      const result = await ms.previewBackfill(ruleId);
      setBackfillData({ ...result, ruleId });
      setBackfillOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const runBackfill = async () => {
    setBusy(true);
    try {
      const result = await ms.executeBackfill(backfillData.ruleId);
      toast.success(`${result.created} Schulungszuweisungen angelegt`);
      setBackfillOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const openOfferEditor = offer => {
    setDraftOffer({
      offerId: offer.offerId,
      name: offer.name || '',
      manualMetadata: {
        displayName: offer.manualMetadata?.displayName || '',
        deepLink: offer.manualMetadata?.deepLink || '',
        notes: offer.manualMetadata?.notes || '',
        courses: (offer.courseIds || []).map(courseId => {
          const existing = (offer.manualMetadata?.courses || []).find(c => c.courseId === courseId);
          return {
            courseId,
            displayName: existing?.displayName || '',
            deepLink: existing?.deepLink || '',
            examIds: existing?.examIds || [],
            examNotes: existing?.examNotes || '',
          };
        }),
      },
    });
    setOfferEditOpen(true);
  };

  const saveOfferMetadata = async () => {
    if (!draftOffer) return;
    setBusy(true);
    try {
      await ms.updateOfferMetadata(draftOffer.offerId, draftOffer.manualMetadata);
      toast.success('Angebotsdetails gespeichert');
      setOfferEditOpen(false);
      await loadAll();
    } finally {
      setBusy(false);
    }
  };

  const openInlineNameDialog = offerId => {
    const offer = offers.find(o => o.offerId === offerId);
    if (!offer) return;
    const draft = {};
    for (const cid of offer.courseIds || []) {
      const meta = (offer.manualMetadata?.courses || []).find(c => c.courseId === cid);
      draft[cid] = meta?.displayName || '';
    }
    setInlineNameDraft(draft);
    setInlineNameOfferId(offerId);
  };

  const handleMcpConnect = async () => {
    setBusy(true);
    try {
      const res = await ms.connectMcp();
      if (res?.authorizeUrl) {
        toast.info('Du wirst zu Memberspot weitergeleitet, um die Verbindung zu autorisieren.');
        window.location.href = res.authorizeUrl;
      }
    } finally {
      setBusy(false);
    }
  };

  const handleMcpDisconnect = async () => {
    setBusy(true);
    try {
      await ms.disconnectMcp();
      toast.success('Memberspot-MCP-Verbindung getrennt');
      const status = await ms.getMcpStatus().catch(() => null);
      setMcpStatus(status);
    } finally {
      setBusy(false);
    }
  };

  const handleMcpSyncNames = async () => {
    setBusy(true);
    try {
      const result = await ms.syncMcpCourseNames();
      const msg = result?.updatedCourses
        ? `${result.updatedCourses} Kurse aktualisiert`
        : 'Keine Änderungen \u2014 alle Namen aktuell';
      toast.success(`Kursnamen synchronisiert (${msg})`);
      await loadAll();
    } finally {
      setBusy(false);
    }
  };

  const saveInlineNames = async () => {
    const offer = offers.find(o => o.offerId === inlineNameOfferId);
    if (!offer) {
      setInlineNameOfferId(null);
      return;
    }
    const mergedCourses = (offer.courseIds || []).map(cid => {
      const existing = (offer.manualMetadata?.courses || []).find(c => c.courseId === cid);
      return {
        courseId: cid,
        displayName: (inlineNameDraft[cid] || '').trim(),
        deepLink: existing?.deepLink || '',
        examIds: existing?.examIds || [],
        examNotes: existing?.examNotes || '',
      };
    });
    setBusy(true);
    try {
      await ms.updateOfferMetadata(offer.offerId, {
        ...(offer.manualMetadata || {}),
        courses: mergedCourses,
      });
      toast.success('Kursnamen gespeichert');
      setInlineNameOfferId(null);
      await loadAll();
    } finally {
      setBusy(false);
    }
  };

  const renderRulesTab = () => (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h6'>Schulungsregeln</Typography>
        <Button startIcon={<AddIcon />} variant='contained' onClick={() => openRuleDialog(null)}>
          Neue Regel
        </Button>
      </Stack>

      {rules.length === 0 && (
        <Alert severity='info'>
          Noch keine Schulungsregeln definiert. Lege eine Regel an, um Mitarbeiter automatisch zu Memberspot
          einzuladen.
        </Alert>
      )}

      <Stack spacing={2}>
        {rules.map(rule => {
          const offer = offersById.get(rule.memberspotOfferId);
          const offerLabel = offer?.manualMetadata?.displayName || offer?.name || rule.memberspotOfferId;
          const roleLabels = (rule.targetRoleIds || [])
            .map(id => roles.find(r => String(r._id) === String(id))?.readableName)
            .filter(Boolean);
          const branchLabels = (rule.targetBranchIds || [])
            .map(id => {
              const b = branches.find(bb => String(bb._id) === String(id));
              return b?.longName || b?.locationName || '';
            })
            .filter(Boolean);
          return (
            <Paper key={rule._id} sx={{ p: 2 }}>
              <Stack direction='row' alignItems='flex-start' justifyContent='space-between'>
                <Box sx={{ flex: 1 }}>
                  <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      {rule.name}
                    </Typography>
                    {!rule.isEnabled && <Chip label='deaktiviert' size='small' color='warning' />}
                    <Chip
                      icon={<SchoolIcon />}
                      label={offerLabel}
                      size='small'
                      variant='outlined'
                    />
                  </Stack>
                  {rule.description && (
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                      {rule.description}
                    </Typography>
                  )}
                  <Stack direction='row' flexWrap='wrap' gap={1}>
                    {roleLabels.length > 0 ? (
                      roleLabels.map(label => <Chip key={label} label={label} size='small' />)
                    ) : (
                      <Chip label='alle Rollen' size='small' variant='outlined' />
                    )}
                    {branchLabels.length > 0 ? (
                      branchLabels.map(label => (
                        <Chip key={label} label={label} size='small' color='info' variant='outlined' />
                      ))
                    ) : (
                      <Chip label='alle Standorte' size='small' color='info' variant='outlined' />
                    )}
                  </Stack>
                  <Typography variant='body2' sx={{ mt: 1 }} color='text.secondary'>
                    Frist {rule.deadlineDaysAfterInvitation} Tage nach Einladung
                    {(() => {
                      const ts =
                        Array.isArray(rule.triggers) && rule.triggers.length > 0
                          ? rule.triggers
                          : rule.trigger
                          ? [rule.trigger]
                          : [];
                      const labels = ts.map(t => {
                        if (t?.type === 'immediate') return 'sofort';
                        if (t?.type === 'afterUserCreation')
                          return `${t.delayDaysAfterUserCreation || 0} Tage nach Nutzeranlage`;
                        if (t?.type === 'afterPrerequisite') {
                          const prereq = rules.find(r => r._id === String(t.prerequisiteRuleId));
                          const days = t.delayDaysAfterPrerequisite || 0;
                          const suffix = days ? ` + ${days} Tage` : '';
                          return prereq
                            ? `nach „${prereq.name}"${suffix}`
                            : `nach Voraussetzungs-Schulung${suffix}`;
                        }
                        return t?.type || '—';
                      });
                      if (labels.length === 0) return '';
                      if (labels.length === 1) return ` · Einladung ${labels[0]}`;
                      return ` · Einladung bei: ${labels.join(' oder ')}`;
                    })()}
                    {rule.applyToExistingUsers ? ' · inkl. Bestands-Accounts' : ''}
                    {rule.consequence?.deactivateMemberspotAccess
                      ? ' · Memberspot wird bei Versäumnis deaktiviert'
                      : ''}
                  </Typography>
                  {(() => {
                    const counts = rule.assignmentCounts || {};
                    const total = Object.values(counts).reduce((s, n) => s + (n || 0), 0);
                    if (total === 0) {
                      return (
                        <Stack direction='row' spacing={1} sx={{ mt: 1.5 }} alignItems='center'>
                          <Chip
                            size='small'
                            variant='outlined'
                            label='Noch keine Einladungen verschickt'
                          />
                          <Button
                            size='small'
                            onClick={() => openAssignmentsDialog(rule)}
                            sx={{ textTransform: 'none' }}
                          >
                            Details
                          </Button>
                        </Stack>
                      );
                    }
                    const chip = (label, value, color) =>
                      value > 0 ? (
                        <Chip
                          key={label}
                          size='small'
                          label={`${value} ${label}`}
                          color={color}
                          variant={color === 'default' ? 'outlined' : 'filled'}
                        />
                      ) : null;
                    return (
                      <Stack
                        direction='row'
                        spacing={1}
                        sx={{ mt: 1.5 }}
                        alignItems='center'
                        flexWrap='wrap'
                        rowGap={1}
                      >
                        {chip('ausstehend', counts.pending, 'default')}
                        {chip('eingeladen', counts.invited, 'info')}
                        {chip('in Bearbeitung', counts.inProgress, 'primary')}
                        {chip('überfällig', counts.overdue, 'error')}
                        {chip('abgeschlossen', counts.completed, 'success')}
                        {chip('fehlgeschlagen', counts.failedSync, 'warning')}
                        <Button
                          size='small'
                          onClick={() => openAssignmentsDialog(rule)}
                          sx={{ textTransform: 'none' }}
                        >
                          {total} Zuweisungen anzeigen
                        </Button>
                      </Stack>
                    );
                  })()}
                </Box>
                <Stack direction='row'>
                  <Tooltip title='Bestehende Mitarbeiter zuweisen'>
                    <IconButton onClick={() => openBackfill(rule._id)}>
                      <SyncIcon />
                    </IconButton>
                  </Tooltip>
                  <IconButton onClick={() => openRuleDialog(rule)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => archiveRule(rule._id)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );

  const renderCatalogTab = () => (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h6'>Memberspot Katalog</Typography>
        {canSync && (
          <Button startIcon={<SyncIcon />} variant='outlined' onClick={handleSync} disabled={busy}>
            Katalog synchronisieren
          </Button>
        )}
      </Stack>

      <Paper
        variant='outlined'
        sx={{
          p: 2,
          mb: 2,
          borderColor: theme =>
            mcpStatus?.status === 'connected'
              ? theme.palette.success.main
              : mcpStatus?.status === 'error'
              ? theme.palette.warning.main
              : theme.palette.divider,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent='space-between'
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                Memberspot MCP-Verbindung
              </Typography>
              {mcpStatus?.status === 'connected' && (
                <Chip color='success' size='small' label='Verbunden' />
              )}
              {mcpStatus?.status === 'pending' && (
                <Chip color='info' size='small' label='Verbindung läuft' />
              )}
              {mcpStatus?.status === 'error' && (
                <Chip color='warning' size='small' label='Fehler' />
              )}
              {(!mcpStatus || mcpStatus.status === 'disconnected') && (
                <Chip color='default' size='small' label='Nicht verbunden' variant='outlined' />
              )}
            </Stack>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
              {mcpStatus?.status === 'connected'
                ? `Verbunden seit ${mcpStatus.connectedAt ? new Date(mcpStatus.connectedAt).toLocaleString('de-DE') : '\u2014'}. Kurs- und Kapitelnamen werden automatisch aus Memberspot übernommen.`
                : 'Verbinde dich einmalig mit deinem Memberspot Admin-Account, damit Kursnamen, Beschreibungen und Kapitelstruktur automatisch geladen werden \u2014 statt sie manuell zu pflegen.'}
            </Typography>
            {mcpStatus?.lastError && (
              <Typography variant='caption' color='warning.main' sx={{ display: 'block', mt: 0.5 }}>
                Letzter Fehler: {mcpStatus.lastError}
              </Typography>
            )}
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            {mcpStatus?.status === 'connected' ? (
              <>
                <Button
                  variant='outlined'
                  startIcon={<SyncIcon />}
                  onClick={handleMcpSyncNames}
                  disabled={busy}
                >
                  Namen jetzt synchronisieren
                </Button>
                <Button variant='text' color='warning' onClick={handleMcpDisconnect} disabled={busy}>
                  Trennen
                </Button>
              </>
            ) : (
              <Button
                variant='contained'
                startIcon={<OpenInNewIcon />}
                onClick={handleMcpConnect}
                disabled={busy}
              >
                Mit Memberspot verbinden
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {offers.length === 0 && (
        <Alert severity='info'>
          Noch keine Angebote im Cache. Synchronisiere den Katalog, sobald die Memberspot Anbindung
          konfiguriert ist.
        </Alert>
      )}

      <Stack spacing={2}>
        {offers.map(offer => (
          <Paper key={offer.offerId} sx={{ p: 2 }}>
            <Stack direction='row' alignItems='flex-start' justifyContent='space-between'>
              <Box sx={{ flex: 1 }}>
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                    {offer.manualMetadata?.displayName || offer.name || offer.offerId}
                  </Typography>
                  {offer.isMissing && (
                    <Chip
                      label='nicht mehr in Memberspot'
                      color='warning'
                      size='small'
                    />
                  )}
                  {offer.manualMetadata?.deepLink && (
                    <Tooltip title='Direktlink öffnen'>
                      <IconButton
                        size='small'
                        component={Link}
                        href={offer.manualMetadata.deepLink}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        <OpenInNewIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant='caption' color='text.secondary'>
                  Offer ID: {offer.offerId} · {(offer.courseIds || []).length} Kurse
                </Typography>
                {(offer.courseIds || []).length > 0 && (
                  <Stack spacing={0.5} sx={{ mt: 1.5, pl: 0 }}>
                    {offer.courseIds.map(cid => {
                      const meta = (offer.manualMetadata?.courses || []).find(
                        c => c.courseId === cid
                      );
                      const manualName = meta?.displayName?.trim();
                      const apiName = meta?.apiName?.trim();
                      const effectiveName = manualName || apiName;
                      const nameSource = manualName ? 'manual' : apiName ? 'api' : null;
                      const deepLink = meta?.deepLink?.trim();
                      const chapters = meta?.chapters || [];
                      return (
                        <Box
                          key={cid}
                          sx={{
                            py: 0.75,
                            px: 1,
                            borderRadius: 1,
                            bgcolor: theme =>
                              theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.04)'
                                : 'rgba(0,0,0,0.03)',
                          }}
                        >
                          <Stack direction='row' alignItems='center' spacing={1}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                                <Typography
                                  variant='body2'
                                  sx={{
                                    fontWeight: effectiveName ? 500 : 400,
                                    color: effectiveName ? 'text.primary' : 'text.secondary',
                                    fontStyle: effectiveName ? 'normal' : 'italic',
                                  }}
                                >
                                  {effectiveName || 'Kein Anzeigename hinterlegt'}
                                </Typography>
                                {nameSource === 'api' && (
                                  <Chip
                                    label='aus Memberspot'
                                    size='small'
                                    color='success'
                                    variant='outlined'
                                  />
                                )}
                                {nameSource === 'manual' && apiName && apiName !== manualName && (
                                  <Tooltip title={`Memberspot-Name: ${apiName}`}>
                                    <Chip
                                      label='Eigener Name'
                                      size='small'
                                      variant='outlined'
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {cid}
                              </Typography>
                            </Box>
                            {deepLink && (
                              <Tooltip title='Kurs in Memberspot öffnen'>
                                <IconButton
                                  size='small'
                                  component={Link}
                                  href={deepLink}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  <OpenInNewIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                          {chapters.length > 0 && (
                            <Stack spacing={0.25} sx={{ mt: 0.75, pl: 1.5 }}>
                              {chapters.map(ch => (
                                <Typography
                                  key={ch.chapterId}
                                  variant='caption'
                                  color='text.secondary'
                                >
                                  · {ch.name || ch.chapterId}
                                  {ch.posts?.length ? ` (${ch.posts.length})` : ''}
                                </Typography>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
              <IconButton onClick={() => openOfferEditor(offer)}>
                <EditIcon />
              </IconButton>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ mb: 1 }}>
        <Typography variant='h4' sx={{ fontWeight: 600 }}>
          Memberspot Schulungen
        </Typography>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Verwalte hier, welche Mitarbeiter automatisch zu welchen Memberspot Kursen eingeladen werden,
        welche Fristen gelten und was bei Versäumnissen geschehen soll.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='overline'>Aktive Regeln</Typography>
              <Typography variant='h5'>{rules.filter(r => r.isEnabled).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='overline'>Inaktive Regeln</Typography>
              <Typography variant='h5'>{rules.filter(r => !r.isEnabled).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='overline'>Angebote im Katalog</Typography>
              <Typography variant='h5'>{offers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant='outlined'>
            <CardContent>
              <Typography variant='overline'>Fehlende Angebote</Typography>
              <Typography variant='h5'>{offers.filter(o => o.isMissing).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label='Regeln' />
        <Tab label='Katalog' />
      </Tabs>

      {tab === 0 ? renderRulesTab() : renderCatalogTab()}

      {/* Rule editor */}
      <Dialog open={ruleDialogOpen} onClose={() => setRuleDialogOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>{draftRuleId ? 'Schulungsregel bearbeiten' : 'Neue Schulungsregel'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label='Name'
              value={draftRule.name}
              onChange={e => setDraftRule(r => ({ ...r, name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label='Beschreibung (intern)'
              value={draftRule.description}
              onChange={e => setDraftRule(r => ({ ...r, description: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!draftRule.isEnabled}
                  onChange={e => setDraftRule(r => ({ ...r, isEnabled: e.target.checked }))}
                />
              }
              label='Regel ist aktiv'
            />

            <Divider />
            <Typography variant='subtitle2'>Zielgruppe</Typography>
            <Autocomplete
              multiple
              options={roles}
              getOptionLabel={r => r.readableName || r.name}
              value={roles.filter(r => draftRule.targetRoleIds.includes(String(r._id)))}
              onChange={(_, value) =>
                setDraftRule(r => ({
                  ...r,
                  targetRoleIds: value.map(v => String(v._id)),
                }))
              }
              renderInput={params => (
                <TextField {...params} label='Rollen (leer = alle Rollen)' />
              )}
            />
            <Autocomplete
              multiple
              options={branches}
              getOptionLabel={b => b?.longName || b?.locationName || ''}
              isOptionEqualToValue={(opt, val) => String(opt._id) === String(val._id)}
              value={branches.filter(b => draftRule.targetBranchIds.includes(String(b._id)))}
              onChange={(_, value) =>
                setDraftRule(r => ({
                  ...r,
                  targetBranchIds: value.map(v => String(v._id)),
                }))
              }
              renderInput={params => (
                <TextField {...params} label='Standorte (leer = alle Standorte)' />
              )}
            />

            <Divider />
            <Typography variant='subtitle2'>Memberspot Inhalte</Typography>
            <FormControl fullWidth>
              <InputLabel>Memberspot Angebot</InputLabel>
              <Select
                value={draftRule.memberspotOfferId}
                label='Memberspot Angebot'
                onChange={e =>
                  setDraftRule(r => {
                    const offer = offersById.get(e.target.value);
                    return {
                      ...r,
                      memberspotOfferId: e.target.value,
                      memberspotCourseIds: offer?.courseIds || [],
                      requiredExamIds: [],
                    };
                  })
                }
              >
                {offers.map(o => (
                  <MenuItem key={o.offerId} value={o.offerId}>
                    {o.manualMetadata?.displayName || o.name || o.offerId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(() => {
              const selectedOffer = offersById.get(draftRule.memberspotOfferId);
              const courseOptions = (selectedOffer?.courseIds || []).map(cid => {
                const meta = (selectedOffer?.manualMetadata?.courses || []).find(
                  c => c.courseId === cid
                );
                const manual = meta?.displayName?.trim();
                const api = meta?.apiName?.trim();
                const label = manual || api || cid;
                return {
                  courseId: cid,
                  label,
                  hasName: !!(manual || api),
                  fromApi: !manual && !!api,
                };
              });
              const selectedCourses = courseOptions.filter(o =>
                draftRule.memberspotCourseIds.includes(o.courseId)
              );
              const courseIdsForExams =
                draftRule.memberspotCourseIds.length > 0
                  ? draftRule.memberspotCourseIds
                  : selectedOffer?.courseIds || [];
              const examOptionMap = new Map();
              const upsertExam = (eid, patch) => {
                if (!examOptionMap.has(eid)) {
                  examOptionMap.set(eid, {
                    examId: eid,
                    name: '',
                    type: '',
                    chapterName: '',
                    courseLabels: new Set(),
                    source: null,
                  });
                }
                const entry = examOptionMap.get(eid);
                if (patch.name && !entry.name) entry.name = patch.name;
                if (patch.type && !entry.type) entry.type = patch.type;
                if (patch.chapterName && !entry.chapterName) entry.chapterName = patch.chapterName;
                if (patch.courseLabel) entry.courseLabels.add(patch.courseLabel);
                if (patch.source) {
                  entry.source = entry.source && entry.source !== patch.source ? 'both' : patch.source;
                }
              };
              for (const cid of courseIdsForExams) {
                const meta = (selectedOffer?.manualMetadata?.courses || []).find(
                  c => c.courseId === cid
                );
                const courseLabel =
                  meta?.displayName?.trim() || meta?.apiName?.trim() || cid;
                for (const eid of meta?.examIds || []) {
                  upsertExam(eid, { courseLabel, source: 'manual' });
                }
                for (const chapter of meta?.chapters || []) {
                  for (const post of chapter.posts || []) {
                    if (!post?.postId) continue;
                    const t = (post.type || '').toLowerCase();
                    if (t === 'lecture' || t === 'subchapter' || t === '') continue;
                    upsertExam(post.postId, {
                      courseLabel,
                      source: 'api',
                      name: post.name || '',
                      type: post.type || '',
                      chapterName: chapter.name || '',
                    });
                  }
                }
              }
              const examOptions = Array.from(examOptionMap.values()).map(o => ({
                examId: o.examId,
                name: o.name,
                type: o.type,
                chapterName: o.chapterName,
                courseLabels: Array.from(o.courseLabels),
                source: o.source || 'manual',
              }));
              for (const eid of draftRule.requiredExamIds) {
                if (!examOptionMap.has(eid)) {
                  examOptions.push({
                    examId: eid,
                    name: '',
                    type: '',
                    chapterName: '',
                    courseLabels: [],
                    source: 'unknown',
                    notCatalogued: true,
                  });
                }
              }
              return (
                <>
                  <Autocomplete
                    multiple
                    options={courseOptions}
                    getOptionLabel={o => o.label}
                    isOptionEqualToValue={(opt, val) => opt.courseId === val.courseId}
                    value={selectedCourses}
                    onChange={(_, value) =>
                      setDraftRule(r => ({
                        ...r,
                        memberspotCourseIds: value.map(v => v.courseId),
                      }))
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...chipProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            {...chipProps}
                            label={
                              option.hasName
                                ? option.label
                                : `Kurs …${option.courseId.slice(-6)} (Name pflegen)`
                            }
                            color={option.hasName ? 'default' : 'warning'}
                            variant={option.hasName ? 'filled' : 'outlined'}
                            size='small'
                          />
                        );
                      })
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography
                            variant='body2'
                            sx={{
                              fontStyle: option.hasName ? 'normal' : 'italic',
                              color: option.hasName ? 'text.primary' : 'text.secondary',
                            }}
                          >
                            {option.hasName ? option.label : 'Kein Anzeigename hinterlegt'}
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {option.courseId}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label='Kurse (für Fortschritts-Tracking)'
                        helperText={
                          selectedOffer
                            ? mcpStatus?.status === 'connected'
                              ? 'Steuert nur, welche Kurse für „abgeschlossen“ zählen. Welche Kurse der Nutzer in Memberspot sieht, ergibt sich aus dem Angebot.'
                              : 'Steuert nur, welche Kurse für „abgeschlossen“ zählen. Verbinde im Katalog-Tab MCP, damit Kursnamen automatisch geladen werden.'
                            : 'Bitte zuerst ein Memberspot Angebot wählen.'
                        }
                      />
                    )}
                    disabled={!selectedOffer}
                  />
                  {selectedOffer && courseOptions.length > 0 && (
                    <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: -1 }}>
                      <Button
                        size='small'
                        startIcon={<EditIcon />}
                        onClick={() => openInlineNameDialog(selectedOffer.offerId)}
                        disabled={!canManage}
                      >
                        Kursnamen pflegen
                      </Button>
                      {courseOptions.some(c => !c.hasName) && (
                        <Typography variant='caption' color='warning.main'>
                          {courseOptions.filter(c => !c.hasName).length} ohne Namen
                        </Typography>
                      )}
                    </Stack>
                  )}
                  {selectedOffer &&
                    courseOptions.length > 1 &&
                    selectedCourses.length > 0 &&
                    selectedCourses.length < courseOptions.length && (
                      <Alert severity='warning' sx={{ mt: 1 }}>
                        <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                          Memberspot gewährt Zugriff auf das ganze Angebot
                        </Typography>
                        <Typography variant='body2'>
                          Du hast {selectedCourses.length} von {courseOptions.length} Kursen des
                          Angebots „
                          {selectedOffer.manualMetadata?.displayName ||
                            selectedOffer.name ||
                            selectedOffer.offerId}
                          " ausgewählt. Diese Auswahl steuert hier nur, welche Kurse für
                          „abgeschlossen" zählen – der eingeladene Nutzer sieht in Memberspot
                          trotzdem alle {courseOptions.length} Kurse des Angebots, weil
                          Memberspot Zugriff immer pro Angebot vergibt.
                        </Typography>
                        <Typography variant='body2' sx={{ mt: 0.5 }}>
                          Wenn der Nutzer wirklich nur die ausgewählten Kurse sehen soll, lege
                          dafür in Memberspot ein eigenes Angebot an (nur mit den gewünschten
                          Kursen) und wähle dieses oben aus.
                        </Typography>
                      </Alert>
                    )}
                  <Autocomplete
                    multiple
                    freeSolo
                    options={examOptions}
                    getOptionLabel={o => {
                      if (typeof o === 'string') return o;
                      return o.name || o.courseLabels?.[0] || o.examId;
                    }}
                    isOptionEqualToValue={(opt, val) =>
                      (typeof opt === 'string' ? opt : opt.examId) ===
                      (typeof val === 'string' ? val : val.examId)
                    }
                    value={draftRule.requiredExamIds.map(
                      eid =>
                        examOptions.find(o => o.examId === eid) || {
                          examId: eid,
                          name: '',
                          type: '',
                          chapterName: '',
                          courseLabels: [],
                          source: 'unknown',
                          notCatalogued: true,
                        }
                    )}
                    onChange={(_, value) =>
                      setDraftRule(r => ({
                        ...r,
                        requiredExamIds: value
                          .map(v => (typeof v === 'string' ? v.trim() : v.examId))
                          .filter(Boolean),
                      }))
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...chipProps } = getTagProps({ index });
                        const examId = typeof option === 'string' ? option : option.examId;
                        const name = typeof option === 'string' ? '' : option.name;
                        const courseLabels =
                          typeof option === 'string' ? [] : option.courseLabels || [];
                        const notCatalogued =
                          typeof option === 'string' ? true : !!option.notCatalogued;
                        const baseLabel =
                          name || courseLabels[0] || `Prüfung …${examId.slice(-6)}`;
                        return (
                          <Chip
                            key={key}
                            {...chipProps}
                            label={baseLabel}
                            color={notCatalogued ? 'warning' : 'default'}
                            variant={notCatalogued ? 'outlined' : 'filled'}
                            size='small'
                          />
                        );
                      })
                    }
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ width: '100%' }}>
                          <Stack direction='row' alignItems='center' spacing={1} flexWrap='wrap'>
                            <Typography variant='body2'>
                              {option.name ||
                                option.courseLabels?.[0] ||
                                `Prüfung …${option.examId.slice(-6)}`}
                            </Typography>
                            {option.type && (
                              <Chip
                                label={option.type}
                                size='small'
                                variant='outlined'
                                color={option.source === 'api' ? 'success' : 'default'}
                              />
                            )}
                            {option.source === 'api' && !option.type && (
                              <Chip label='aus Memberspot' size='small' color='success' variant='outlined' />
                            )}
                          </Stack>
                          {(option.chapterName || option.courseLabels?.length > 0) && (
                            <Typography variant='caption' color='text.secondary'>
                              {[option.courseLabels?.[0], option.chapterName].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ fontFamily: 'monospace', display: 'block' }}
                          >
                            {option.examId}
                          </Typography>
                          {option.notCatalogued && (
                            <Typography
                              variant='caption'
                              color='warning.main'
                              sx={{ display: 'block' }}
                            >
                              noch nicht im Katalog hinterlegt
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label='Erforderliche Prüfungen'
                        placeholder='Prüfung wählen oder ID eingeben'
                        helperText={
                          examOptions.length > 0
                            ? 'Aus den ausgewählten Kursen verfügbare Prüfungen \u2014 wenn leer, gilt die Schulung als abgeschlossen, sobald alle Kurse 100% Fortschritt haben.'
                            : 'Noch keine Prüfungen aus Memberspot bekannt. Verbinde im Katalog-Tab MCP oder tippe Prüfungs-IDs direkt ein.'
                        }
                      />
                    )}
                  />
                </>
              );
            })()}

            <Divider />
            <Stack direction='row' alignItems='center' justifyContent='space-between'>
              <Typography variant='subtitle2'>Auslöser & Frist</Typography>
              <Button
                size='small'
                startIcon={<AddIcon />}
                onClick={() =>
                  setDraftRule(r => ({
                    ...r,
                    triggers: [
                      ...(r.triggers || []),
                      { type: 'afterUserCreation', delayDaysAfterUserCreation: 0 },
                    ],
                  }))
                }
              >
                Weiteren Auslöser hinzufügen
              </Button>
            </Stack>
            {(draftRule.triggers?.length || 0) > 1 && (
              <Typography variant='caption' color='text.secondary'>
                Die Schulung wird eingeladen, sobald <b>einer</b> der Auslöser greift (die
                Einladung wird pro Mitarbeiter nur einmal verschickt).
              </Typography>
            )}
            {(draftRule.triggers || []).map((trigger, idx) => {
              const updateTrigger = patch =>
                setDraftRule(r => ({
                  ...r,
                  triggers: r.triggers.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
                }));
              const removeTrigger = () =>
                setDraftRule(r => ({
                  ...r,
                  triggers:
                    r.triggers.length > 1
                      ? r.triggers.filter((_, i) => i !== idx)
                      : r.triggers,
                }));
              return (
                <Paper
                  key={idx}
                  variant='outlined'
                  sx={{ p: 1.5, position: 'relative' }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{ mb: 1 }}
                  >
                    <Typography variant='caption' color='text.secondary'>
                      Auslöser {idx + 1}
                      {(draftRule.triggers?.length || 0) > 1 ? ` von ${draftRule.triggers.length}` : ''}
                    </Typography>
                    {(draftRule.triggers?.length || 0) > 1 && (
                      <IconButton
                        size='small'
                        onClick={removeTrigger}
                        aria-label='Auslöser entfernen'
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    )}
                  </Stack>
                  <Stack spacing={1.5}>
                    <FormControl fullWidth size='small'>
                      <InputLabel>Auslöser-Typ</InputLabel>
                      <Select
                        value={trigger.type}
                        label='Auslöser-Typ'
                        onChange={e => updateTrigger({ type: e.target.value })}
                      >
                        <MenuItem value='immediate'>
                          Sofort (sobald Mitarbeiter zur Regel passt)
                        </MenuItem>
                        <MenuItem value='afterUserCreation'>
                          Nach Anlage des Mitarbeiters
                        </MenuItem>
                        <MenuItem value='afterPrerequisite'>
                          Nach Abschluss einer Voraussetzungs-Schulung
                        </MenuItem>
                      </Select>
                    </FormControl>
                    {trigger.type === 'immediate' ? (
                      <Typography variant='caption' color='text.secondary'>
                        Sobald ein Mitarbeiter die Rollen-/Standort-Kriterien erfüllt, wird er
                        ohne Wartezeit eingeladen. Für bestehende Accounts zusätzlich „Auch auf
                        bestehende Accounts anwenden" aktivieren.
                      </Typography>
                    ) : trigger.type === 'afterUserCreation' ? (
                      <TextField
                        label='Tage nach Nutzeranlage'
                        type='number'
                        size='small'
                        value={trigger.delayDaysAfterUserCreation || 0}
                        onChange={e =>
                          updateTrigger({
                            delayDaysAfterUserCreation: Math.max(
                              0,
                              Number(e.target.value) || 0
                            ),
                          })
                        }
                      />
                    ) : (
                      <>
                        <FormControl fullWidth size='small'>
                          <InputLabel>Voraussetzungs-Regel</InputLabel>
                          <Select
                            value={trigger.prerequisiteRuleId || ''}
                            label='Voraussetzungs-Regel'
                            onChange={e =>
                              updateTrigger({ prerequisiteRuleId: e.target.value })
                            }
                          >
                            {rules
                              .filter(r => r._id !== draftRuleId)
                              .map(r => (
                                <MenuItem key={r._id} value={r._id}>
                                  {r.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label='Tage nach Abschluss der Voraussetzung'
                          type='number'
                          size='small'
                          value={trigger.delayDaysAfterPrerequisite || 0}
                          onChange={e =>
                            updateTrigger({
                              delayDaysAfterPrerequisite: Math.max(
                                0,
                                Number(e.target.value) || 0
                              ),
                            })
                          }
                        />
                      </>
                    )}
                  </Stack>
                </Paper>
              );
            })}
            <TextField
              label='Frist (Tage nach Einladung)'
              type='number'
              value={draftRule.deadlineDaysAfterInvitation}
              onChange={e =>
                setDraftRule(r => ({
                  ...r,
                  deadlineDaysAfterInvitation: Math.max(1, Number(e.target.value) || 1),
                }))
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!draftRule.setMemberspotExpiryFromDeadline}
                  onChange={e =>
                    setDraftRule(r => ({ ...r, setMemberspotExpiryFromDeadline: e.target.checked }))
                  }
                />
              }
              label='Memberspot Ablaufdatum aus Frist übernehmen'
            />

            <Paper
              variant='outlined'
              sx={{
                p: 1.5,
                bgcolor: theme =>
                  draftRule.applyToExistingUsers
                    ? theme.palette.mode === 'dark'
                      ? 'rgba(255,193,7,0.08)'
                      : 'rgba(255,193,7,0.12)'
                    : 'transparent',
                borderColor: theme =>
                  draftRule.applyToExistingUsers ? theme.palette.warning.main : theme.palette.divider,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={!!draftRule.applyToExistingUsers}
                    onChange={e =>
                      setDraftRule(r => ({ ...r, applyToExistingUsers: e.target.checked }))
                    }
                  />
                }
                label='Auch auf bestehende Accounts anwenden'
              />
              <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
                {draftRule.applyToExistingUsers
                  ? 'Beim Speichern werden sofort alle bereits existierenden Accounts mit passender Rolle/Standort eingeladen \u2014 zusätzlich zu neuen Accounts. Achtung: das kann zu vielen Einladungen auf einen Schlag führen.'
                  : 'Standard: Nur Accounts, die nach dem Anlegen dieser Regel erstellt werden, bekommen diese Schulung. Bereits bestehende Mitarbeiter sind ausgenommen.'}
              </Typography>
            </Paper>

            <Divider />
            <Typography variant='subtitle2'>Erinnerungen</Typography>
            <Typography variant='caption' color='text.secondary'>
              Wer wird wie viele Tage vor Ablauf der Frist erinnert? „Ausgewählte Rollen"
              verwendet die Rollen, die du weiter unten bei „Benachrichtigungs-Rollen"
              auswählst (mit automatischem Standort-Bezug).
            </Typography>
            <Stack spacing={1}>
              {draftRule.reminders.map((reminder, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'grid',
                    gap: 1,
                    alignItems: 'center',
                    gridTemplateColumns: {
                      xs: '1fr auto',
                      sm: 'minmax(180px, 1fr) 140px auto',
                    },
                    gridTemplateAreas: {
                      xs: `"kind delete" "days delete"`,
                      sm: `"kind days delete"`,
                    },
                  }}
                >
                  <FormControl size='small' sx={{ gridArea: 'kind', minWidth: 0 }} fullWidth>
                    <Select
                      value={reminder.kind}
                      onChange={e =>
                        setDraftRule(r => ({
                          ...r,
                          reminders: r.reminders.map((rem, i) =>
                            i === idx ? { ...rem, kind: e.target.value } : rem
                          ),
                        }))
                      }
                    >
                      <MenuItem value='employee'>Mitarbeiter (Trainee)</MenuItem>
                      <MenuItem value='manager'>Ausgewählte Rollen</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size='small'
                    type='number'
                    label='Tage vor Frist'
                    value={reminder.daysBeforeDue}
                    onChange={e =>
                      setDraftRule(r => ({
                        ...r,
                        reminders: r.reminders.map((rem, i) =>
                          i === idx
                            ? { ...rem, daysBeforeDue: Math.max(0, Number(e.target.value) || 0) }
                            : rem
                        ),
                      }))
                    }
                    sx={{ gridArea: 'days', minWidth: 0, width: '100%' }}
                  />
                  <IconButton
                    sx={{ gridArea: 'delete', justifySelf: 'end' }}
                    onClick={() =>
                      setDraftRule(r => ({
                        ...r,
                        reminders: r.reminders.filter((_, i) => i !== idx),
                      }))
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                size='small'
                startIcon={<AddIcon />}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                onClick={() =>
                  setDraftRule(r => ({
                    ...r,
                    reminders: [...r.reminders, { kind: 'employee', daysBeforeDue: 1 }],
                  }))
                }
              >
                Erinnerung hinzufügen
              </Button>
            </Stack>

            <Divider />
            <Typography variant='subtitle2'>Konsequenzen bei Versäumnis</Typography>
            <Autocomplete
              multiple
              options={roles}
              getOptionLabel={r => r.readableName || r.name}
              isOptionEqualToValue={(o, v) => String(o._id) === String(v._id)}
              value={roles.filter(r =>
                (draftRule.consequence.acknowledgementRoleIds || []).includes(String(r._id))
              )}
              onChange={(_, value) =>
                setDraftRule(r => ({
                  ...r,
                  consequence: {
                    ...r.consequence,
                    acknowledgementRoleIds: value.map(v => String(v._id)),
                  },
                }))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label='Rollen, die das Mitarbeitergespräch bestätigen müssen'
                  helperText='Personen mit einer dieser Rollen können das Gespräch abhaken. Hat der Mitarbeiter einen zugewiesenen Standort, dürfen das nur Rollen-Träger an genau diesem Standort. Leer lassen = es ist gar keine Bestätigung nötig.'
                />
              )}
            />
            <Autocomplete
              multiple
              options={roles}
              getOptionLabel={r => r.readableName || r.name}
              isOptionEqualToValue={(o, v) => String(o._id) === String(v._id)}
              value={roles.filter(r =>
                (draftRule.consequence.notifyRoleIds || []).includes(String(r._id))
              )}
              onChange={(_, value) =>
                setDraftRule(r => ({
                  ...r,
                  consequence: {
                    ...r.consequence,
                    notifyRoleIds: value.map(v => String(v._id)),
                  },
                }))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label='Benachrichtigungs-Rollen (Erinnerungen & Eskalation)'
                  helperText='Diese Rollen werden bei „Ausgewählte Rollen"-Erinnerungen sowie bei überschrittener Frist informiert. Mit zugewiesenem Standort werden nur Rollen-Träger an diesem Standort benachrichtigt; ohne zugewiesenen Standort alle Rollen-Träger.'
                />
              )}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!draftRule.consequence.deactivateMemberspotAccess}
                  onChange={e =>
                    setDraftRule(r => ({
                      ...r,
                      consequence: {
                        ...r.consequence,
                        deactivateMemberspotAccess: e.target.checked,
                      },
                    }))
                  }
                />
              }
              label='Memberspot Zugang bei Versäumnis automatisch deaktivieren'
            />
            <TextField
              label='Interne Notiz bei Versäumnis'
              value={draftRule.consequence.internalRestrictionNote || ''}
              onChange={e =>
                setDraftRule(r => ({
                  ...r,
                  consequence: {
                    ...r.consequence,
                    internalRestrictionNote: e.target.value,
                  },
                }))
              }
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRuleDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={saveRule} variant='contained' disabled={busy}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backfill preview */}
      <Dialog open={backfillOpen} onClose={() => setBackfillOpen(false)} maxWidth='sm' fullWidth>
        <DialogTitle>Bestehende Mitarbeiter zuweisen</DialogTitle>
        <DialogContent dividers>
          {backfillData?.eligibleUsers?.length > 0 ? (
            <>
              <Alert severity='info' sx={{ mb: 2 }}>
                {backfillData.eligibleUsers.length} Mitarbeiter werden zur Schulung eingeladen.
              </Alert>
              <Stack spacing={1}>
                {backfillData.eligibleUsers.slice(0, 200).map(u => (
                  <Typography key={u._id} variant='body2'>
                    {[u.firstName, u.lastName].filter(Boolean).join(' ') || u.username}{' '}
                    {u.email && <span style={{ opacity: 0.7 }}>· {u.email}</span>}
                    {u.branchNames?.length > 0 && (
                      <span style={{ opacity: 0.7 }}> · {u.branchNames.join(', ')}</span>
                    )}
                  </Typography>
                ))}
                {backfillData.eligibleUsers.length > 200 && (
                  <Typography variant='caption' color='text.secondary'>
                    … weitere werden ebenfalls eingeladen.
                  </Typography>
                )}
              </Stack>
            </>
          ) : (
            <Alert severity='info'>
              Keine bestehenden Mitarbeiter erfüllen aktuell die Kriterien dieser Regel.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackfillOpen(false)}>Abbrechen</Button>
          <Button
            variant='contained'
            disabled={!backfillData?.eligibleUsers?.length || busy}
            onClick={runBackfill}
          >
            Jetzt zuweisen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!assignmentsDialogRule}
        onClose={() => {
          setAssignmentsDialogRule(null);
          setAssignmentsDialogData(null);
        }}
        maxWidth='lg'
        fullWidth
      >
        <DialogTitle>
          Zuweisungen{assignmentsDialogRule ? ` · ${assignmentsDialogRule.name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {assignmentsDialogLoading && !assignmentsDialogData ? (
            <Typography variant='body2' color='text.secondary'>
              Lädt…
            </Typography>
          ) : assignmentsDialogData?.assignments?.length > 0 ? (
            <Stack spacing={1}>
              {assignmentsDialogData.assignments.map(a => {
                const name =
                  [a.userFirstName, a.userLastName].filter(Boolean).join(' ') ||
                  a.userUsername ||
                  a.userEmail ||
                  '—';
                const statusMeta = {
                  pending: { label: 'ausstehend', color: 'default' },
                  invited: { label: 'eingeladen', color: 'info' },
                  inProgress: { label: 'in Bearbeitung', color: 'primary' },
                  overdue: { label: 'überfällig', color: 'error' },
                  completed: { label: 'abgeschlossen', color: 'success' },
                  failedSync: { label: 'Sync fehlgeschlagen', color: 'warning' },
                  archived: { label: 'archiviert', color: 'default' },
                }[a.status] || { label: a.status, color: 'default' };
                return (
                  <Paper
                    key={a._id}
                    variant='outlined'
                    sx={{
                      p: 1.5,
                      borderLeft: theme =>
                        a.status === 'overdue'
                          ? `4px solid ${theme.palette.error.main}`
                          : a.status === 'failedSync'
                          ? `4px solid ${theme.palette.warning.main}`
                          : a.status === 'completed'
                          ? `4px solid ${theme.palette.success.main}`
                          : `4px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ sm: 'center' }}
                      justifyContent='space-between'
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                          <Typography variant='body2' sx={{ fontWeight: 600 }}>
                            {name}
                          </Typography>
                          <Chip size='small' label={statusMeta.label} color={statusMeta.color} />
                          {a.memberspotUid && (
                            <Tooltip title={`Memberspot uid: ${a.memberspotUid}`}>
                              <Chip size='small' variant='outlined' label='Memberspot bekannt' />
                            </Tooltip>
                          )}
                        </Stack>
                        {a.userEmail && (
                          <Typography variant='caption' color='text.secondary'>
                            {a.userEmail}
                          </Typography>
                        )}
                        <Stack direction='row' spacing={2} sx={{ mt: 0.5 }} flexWrap='wrap'>
                          {a.invitedAt && (
                            <Typography variant='caption' color='text.secondary'>
                              eingeladen: {dayjs(a.invitedAt).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                          )}
                          {a.dueAt && (
                            <Typography variant='caption' color='text.secondary'>
                              fällig: {dayjs(a.dueAt).format('DD.MM.YYYY')}
                            </Typography>
                          )}
                          {a.completedAt && (
                            <Typography variant='caption' color='success.main'>
                              abgeschlossen: {dayjs(a.completedAt).format('DD.MM.YYYY')}
                            </Typography>
                          )}
                          {a.managerConversation?.acknowledgedAt && (
                            <Typography variant='caption' color='text.secondary'>
                              Gespräch:{' '}
                              {dayjs(a.managerConversation.acknowledgedAt).format(
                                'DD.MM.YYYY'
                              )}
                            </Typography>
                          )}
                        </Stack>
                        {a.lastResentAt && (
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ display: 'block', mt: 0.5 }}
                          >
                            zuletzt erneut gesendet:{' '}
                            {dayjs(a.lastResentAt).format('DD.MM.YYYY HH:mm')}
                            {a.resentCount > 1 ? ` (${a.resentCount}×)` : ''}
                          </Typography>
                        )}
                        {a.lastSyncError?.message && (
                          <Alert severity='warning' sx={{ mt: 1, py: 0 }}>
                            {a.lastSyncError.message}
                            {a.lastSyncError.code ? ` (${a.lastSyncError.code})` : ''}
                          </Alert>
                        )}
                      </Box>
                      {a.status !== 'completed' && a.status !== 'archived' && (
                        <Box sx={{ flexShrink: 0 }}>
                          <Tooltip
                            title={
                              a.status === 'pending' || a.status === 'failedSync'
                                ? 'Einladung an Memberspot senden (setzt Frist neu)'
                                : 'Einladungs-Mail über Memberspot erneut versenden (Frist bleibt)'
                            }
                          >
                            <span>
                              <Button
                                size='small'
                                variant='outlined'
                                startIcon={<SendIcon />}
                                onClick={() => askResendAssignmentInvitation(a)}
                                disabled={resendingAssignmentId === a._id}
                              >
                                {resendingAssignmentId === a._id
                                  ? 'Sende…'
                                  : a.status === 'pending' || a.status === 'failedSync'
                                  ? 'Einladen'
                                  : 'Einladung erneut'}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Alert severity='info'>
              Diese Regel hat bisher keine Zuweisungen produziert. Sobald passende Mitarbeiter
              existieren und der Trigger feuert, erscheinen sie hier.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={refreshAssignmentsDialog}
            disabled={assignmentsDialogLoading}
            startIcon={<SyncIcon />}
          >
            Aktualisieren
          </Button>
          <Button
            onClick={() => {
              setAssignmentsDialogRule(null);
              setAssignmentsDialogData(null);
            }}
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!resendConfirm}
        onClose={() => {
          if (!resendingAssignmentId) setResendConfirm(null);
        }}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Einladung erneut senden?</DialogTitle>
        <DialogContent dividers>
          {resendConfirm && (
            <Stack spacing={1.5}>
              <Typography variant='body2'>
                Memberspot wird gebeten, die Einladungs-Mail erneut zu versenden an:
              </Typography>
              <Paper variant='outlined' sx={{ p: 1.5 }}>
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  {[resendConfirm.userFirstName, resendConfirm.userLastName]
                    .filter(Boolean)
                    .join(' ') ||
                    resendConfirm.userUsername ||
                    resendConfirm.userEmail ||
                    '—'}
                </Typography>
                {resendConfirm.userEmail && (
                  <Typography variant='caption' color='text.secondary'>
                    {resendConfirm.userEmail}
                  </Typography>
                )}
              </Paper>
              {resendConfirm.status === 'pending' || resendConfirm.status === 'failedSync' ? (
                <Alert severity='info' sx={{ py: 0.5 }}>
                  Die Frist wird auf Basis der Regel neu gesetzt, da der Mitarbeiter bisher nicht
                  erfolgreich eingeladen wurde.
                </Alert>
              ) : (
                <Alert severity='info' sx={{ py: 0.5 }}>
                  Die ursprüngliche Frist bleibt erhalten – es wird nur erneut eine
                  Einladungs-Mail über Memberspot ausgelöst.
                </Alert>
              )}
              {resendConfirm.resentCount > 0 && (
                <Typography variant='caption' color='text.secondary'>
                  Diese Einladung wurde bereits {resendConfirm.resentCount}× erneut gesendet
                  {resendConfirm.lastResentAt
                    ? ` (zuletzt am ${dayjs(resendConfirm.lastResentAt).format(
                        'DD.MM.YYYY HH:mm'
                      )})`
                    : ''}
                  .
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendConfirm(null)} disabled={!!resendingAssignmentId}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            color='primary'
            startIcon={<SendIcon />}
            onClick={confirmResendAssignmentInvitation}
            disabled={!!resendingAssignmentId}
          >
            {resendingAssignmentId ? 'Sende…' : 'Einladung senden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inline course-name editor (quick, shown from the rule editor) */}
      <Dialog
        open={!!inlineNameOfferId}
        onClose={() => setInlineNameOfferId(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Kursnamen pflegen</DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            {mcpStatus?.status === 'connected'
              ? 'Kursnamen kommen aus Memberspot. Hier kannst du sie für FunZone überschreiben — z.B. wenn der interne Name kürzer oder anders sein soll.'
              : 'Memberspot-MCP ist nicht verbunden. Hinterlege hier Anzeigenamen lokal \u2014 oder verbinde MCP im Katalog-Tab, damit Namen automatisch geladen werden.'}
          </Typography>
          <Stack spacing={1.5}>
            {(() => {
              const offer = offers.find(o => o.offerId === inlineNameOfferId);
              if (!offer) return null;
              return (offer.courseIds || []).map(cid => {
                const meta = (offer.manualMetadata?.courses || []).find(c => c.courseId === cid);
                const apiName = meta?.apiName?.trim();
                return (
                  <Box
                    key={cid}
                    sx={{
                      display: 'grid',
                      gap: 1,
                      alignItems: 'center',
                      gridTemplateColumns: { xs: '1fr', sm: 'minmax(0,1fr) auto' },
                    }}
                  >
                    <TextField
                      size='small'
                      label='Anzeigename (optional)'
                      placeholder={apiName || 'eigener Name'}
                      value={inlineNameDraft[cid] || ''}
                      onChange={e =>
                        setInlineNameDraft(d => ({ ...d, [cid]: e.target.value }))
                      }
                      helperText={
                        apiName ? `Memberspot-Name: ${apiName}` : undefined
                      }
                      fullWidth
                    />
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ fontFamily: 'monospace', justifySelf: { sm: 'end' } }}
                    >
                      {cid}
                    </Typography>
                  </Box>
                );
              });
            })()}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInlineNameOfferId(null)} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant='contained' onClick={saveInlineNames} disabled={busy || !canManage}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offer metadata editor */}
      <Dialog open={offerEditOpen} onClose={() => setOfferEditOpen(false)} maxWidth='md' fullWidth>
        <DialogTitle>Memberspot Angebot bearbeiten</DialogTitle>
        <DialogContent dividers>
          {draftOffer && (
            <Stack spacing={2}>
              <TextField
                label='Angezeigter Name'
                value={draftOffer.manualMetadata.displayName}
                onChange={e =>
                  setDraftOffer(o => ({
                    ...o,
                    manualMetadata: { ...o.manualMetadata, displayName: e.target.value },
                  }))
                }
                placeholder={draftOffer.name}
                fullWidth
              />
              <TextField
                label='Direktlink zum Angebot in Memberspot'
                value={draftOffer.manualMetadata.deepLink}
                onChange={e =>
                  setDraftOffer(o => ({
                    ...o,
                    manualMetadata: { ...o.manualMetadata, deepLink: e.target.value },
                  }))
                }
                helperText='Wird im Status- und Self-Service-View angezeigt.'
                fullWidth
              />
              <TextField
                label='Interne Notizen'
                value={draftOffer.manualMetadata.notes}
                onChange={e =>
                  setDraftOffer(o => ({
                    ...o,
                    manualMetadata: { ...o.manualMetadata, notes: e.target.value },
                  }))
                }
                multiline
                minRows={2}
                fullWidth
              />

              <Divider />
              <Typography variant='subtitle2'>Kurse</Typography>
              {(draftOffer.manualMetadata.courses || []).map((course, idx) => (
                <Paper key={course.courseId} variant='outlined' sx={{ p: 2 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Course ID: {course.courseId}
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      size='small'
                      label='Angezeigter Name'
                      value={course.displayName}
                      onChange={e =>
                        setDraftOffer(o => ({
                          ...o,
                          manualMetadata: {
                            ...o.manualMetadata,
                            courses: o.manualMetadata.courses.map((c, i) =>
                              i === idx ? { ...c, displayName: e.target.value } : c
                            ),
                          },
                        }))
                      }
                    />
                    <TextField
                      size='small'
                      label='Direktlink zum Kurs'
                      value={course.deepLink}
                      onChange={e =>
                        setDraftOffer(o => ({
                          ...o,
                          manualMetadata: {
                            ...o.manualMetadata,
                            courses: o.manualMetadata.courses.map((c, i) =>
                              i === idx ? { ...c, deepLink: e.target.value } : c
                            ),
                          },
                        }))
                      }
                    />
                    <TextField
                      size='small'
                      label='Exam IDs (komma-getrennt)'
                      value={csvFromTokens(course.examIds)}
                      onChange={e =>
                        setDraftOffer(o => ({
                          ...o,
                          manualMetadata: {
                            ...o.manualMetadata,
                            courses: o.manualMetadata.courses.map((c, i) =>
                              i === idx
                                ? { ...c, examIds: tokensFromCsv(e.target.value) }
                                : c
                            ),
                          },
                        }))
                      }
                    />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferEditOpen(false)}>Abbrechen</Button>
          <Button variant='contained' onClick={saveOfferMetadata} disabled={busy}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
